import {
  CommonButton,
  ConfirmationModal,
  ControlledForm,
  ErrorPage,
  FloatingMenu,
  Icon,
  IconButton,
  InfoCardInner,
  LoadingPage,
  Select,
  TConfirmationModalProps,
  TInfoCardSectionProps,
  TMenuItemProps,
  Tooltip
} from '@chocolate-soup-inc/cs-frontend-components';
import { joiResolver } from '@hookform/resolvers/joi';
import _ from 'lodash';
import { MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { serializeError } from 'serialize-error';
import { getUpdateShipmentExtraAttributesSchema } from '../../../entities/shipments/schema';
import { TShipmentStatuses, TShipStationAddressVerified, useDeleteShipmentMutation, useRecreateShipmentMutation, useResyncShipmentWithShipStationMutation, useUpdateShipmentMutation } from '../../../generated/graphql';
import { ShipmentModal } from './ShipmentModal';

import { readableDate, Storage } from '@chocolate-soup-inc/cs-api-consumer-utils';
import clsx from 'clsx';
import { AlertTooltipDismiss } from '../../../components/alerts/AlertTooltipDismiss';
import { CountryName } from '../../../components/coutryName/CountryName';
import { EasyJsonViewer } from '../../../components/easyJsonViewer/EasyJsonViewer';
import { useFilter } from '../../../contexts/filters';
import { useQueryAllAddressesByCompanyId } from '../../../entities/addresses/queries';
import { useAuthContextUserEmail } from '../../../entities/shared/auth';
import { TShipmentType, useQueryOneShipment } from '../../../entities/shipments/queries';
import { generateCUSMAformPDF } from '../../../entities/shipments/shared';
import { TrackingLink } from '../../../entities/shipments/utils';
import { EditPackageInfoModal } from '../EditPackageInfoModal/EditPackageInfoModal';
import styles from './ShipmentModalDetails.module.scss';

type TShipmentExtraAttributesForm = Pick<TShipmentType, 'rack' | 'notes'>;

export const ShipmentModalDetails = () => {
  const { filtersModalMode } = useFilter();
  useEffect(() => {
    filtersModalMode();
  }, []); // eslint-disable-line

  const { shipmentId = '0' } = useParams();
  const navigate = useNavigate();

  const userEmail = useAuthContextUserEmail();
  const [updateShipment] = useUpdateShipmentMutation();

  const [confirmationProps, setConfirmationProps] = useState<Omit<TConfirmationModalProps, 'closeModal' | 'confirmLoading' | 'onCancelClick'>>();

  const {
    data: shipment,
    error,
    loading,
    refetch
  } = useQueryOneShipment({
    id: shipmentId
  });
  //console.log('shipment?:', shipment);

  // --- ADDRESS OVERRIDE ---
  const [addressOverride, setAddressOverride] = useState<string | null | undefined>(undefined);

  const {
    data: availableAddresses
    //error: availableAddressesError,
    //loading: availableAddressesLoading,
  } = useQueryAllAddressesByCompanyId({
    companyId: shipment.companyId
  });

  const onAddressOverride = useCallback(async () => {
    try {
      await updateShipment({
        variables: {
          id: shipment.id,
          companyId: shipment.companyId,
          version: shipment._version,
          input: { addressId: addressOverride, oldAddressId: shipment.oldAddressId ?? shipment.addressId }
        }
      });
      toast.success('The address to use was successfully changed.');
    } catch (error) {
      console.error(serializeError(error));
      toast.error('There was an error changing which address to use.');
    } finally {
      setAddressLoading(false);
      setAddressOverride(null);
    }
    try {
      await refetch();
    } catch (error) {
      console.error('Could not refresh shipment details:', serializeError(error));
    }
  }, [updateShipment, addressOverride, shipment, refetch]);

  const onRestoreAddress = useCallback(async () => {
    try {
      await updateShipment({
        variables: {
          id: shipment.id,
          companyId: shipment.companyId,
          version: shipment._version,
          input: { addressId: shipment.oldAddressId, oldAddressId: null }
        }
      });
      toast.success('The address to use was successfully restored.');
    } catch (error) {
      console.error(serializeError(error));
      toast.error('There was an error changing which address to use.');
    } finally {
      setAddressLoading(false);
      setAddressOverride(null);
    }
    try {
      await refetch();
    } catch (error) {
      console.error('Could not refresh shipment details:', serializeError(error));
    }
  }, [updateShipment, shipment, refetch]);

  const availableAddressesOpts = useMemo(
    () =>
      availableAddresses.map((address) => ({
        label: `${address.parent?.fullName ?? address.parent?.name} (${address.city}, ${address.state}, ${address.country})`,
        value: address.id as string
      })) || [],
    [availableAddresses]
  );
  // --- ADDRESS OVERRIDE ---

  const closeModal = useCallback(() => {
    navigate(generatePath('../../'));
  }, [navigate]);

  const shippingDate = useMemo(() => {
    return shipment.actualShippingDate || shipment.shippingDate || undefined;
  }, [shipment.actualShippingDate, shipment.shippingDate]);

  const [deleteShipment, { loading: deleteLoading }] = useDeleteShipmentMutation();

  const onActualDelete = useCallback(async () => {
    if (!shipment.id || !shipment.companyId || !shipment._version) return;
    try {
      await deleteShipment({ variables: { id: shipment.id, companyId: shipment.companyId, version: shipment._version } });
      closeModal();
    } catch (error) {
      console.error(serializeError(error));
      toast.error('There was an error deleting the shipment.');
    } finally {
      setConfirmationProps(undefined);
    }
  }, [closeModal, deleteShipment, shipment._version, shipment.companyId, shipment.id]);

  const onDeleteClick = useCallback(() => {
    setConfirmationProps({
      headline: 'Delete Shipment?',
      supportingText: `Are you sure you want to delete this shipment from company ${shipment.company?.name}?`,
      confirmLabel: 'Delete',
      onConfirmClick: onActualDelete
    });
  }, [shipment, onActualDelete]);

  const [recreateShipment, { loading: recreateLoading }] = useRecreateShipmentMutation();

  const onActualRecreate = useCallback(async () => {
    if (!shipment.id || !shipment.companyId) return;
    try {
      await recreateShipment({ variables: { id: shipment.id, companyId: shipment.companyId } });
      closeModal();
    } catch (error) {
      console.error(serializeError(error));
      toast.error('There was an error recreating the shipment.');
    } finally {
      setConfirmationProps(undefined);
    }
  }, [closeModal, recreateShipment, shipment.companyId, shipment.id]);

  const onRecreateClick = useCallback(() => {
    setConfirmationProps({
      headline: 'Recreate Shipment?',
      supportingText: `Are you sure you want to recreate this shipment gifts?`,
      confirmLabel: 'Recreate',
      onConfirmClick: onActualRecreate
    });
  }, [onActualRecreate]);

  const topComponentMenuOptions = useMemo(() => {
    const options: TMenuItemProps[] = [];

    if (
      [
        //TShipmentStatuses.ReadyToShip,
        //TShipmentStatuses.PrintingLabel,
        TShipmentStatuses.LabelPrinted,
        TShipmentStatuses.Shipped
      ].includes(shipment.status) &&
      ['usa'].includes(shipment.address?.country?.toLowerCase() as string)
    )
      options.push({
        label: 'Print CUSMA (USA) form (UPS)',
        leadingIcon: 'file_save',
        loading: recreateLoading,
        type: 'text',
        onClick: async () => {
          try {
            await generateCUSMAformPDF(shipment);
          } catch (error) {
            console.error(serializeError(error));
            toast.error('There was an error generating the CUSMA form.');
          }
        }
      });

    if (
      shipment.status &&
      [TShipmentStatuses.PrintingLabel, TShipmentStatuses.LabelPrinted, TShipmentStatuses.Shipped, TShipmentStatuses.Attention, TShipmentStatuses.Delivered, TShipmentStatuses.Error, TShipmentStatuses.Canceled].includes(shipment.status)
    )
      options.push({
        disabled: deleteLoading,
        label: 'Recreate Shipment',
        leadingIcon: 'control_point_duplicate',
        loading: recreateLoading,
        type: 'text',
        onClick: onRecreateClick
      });

    if (shipment.status && [TShipmentStatuses.Packaging, TShipmentStatuses.ReadyToShip, TShipmentStatuses.LabelPrinted, TShipmentStatuses.PrintingLabel].includes(shipment.status))
      options.push({
        label: 'Delete Shipment',
        loading: deleteLoading || recreateLoading,
        leadingIcon: 'delete',
        onClick: onDeleteClick,
        type: 'text'
      });

    return options;
  }, [deleteLoading, onDeleteClick, onRecreateClick, recreateLoading, shipment]);

  const [topComponentMenuOpen, setTopComponentMenuOpen] = useState<boolean>(false);

  const labelDataPresent = useMemo(() => {
    return shipment.label?.labelFile && shipment.label.labelFile.key && shipment.label.labelFile.bucket && shipment.label.labelFile.region;
  }, [shipment]);

  const [openLabelLoading, setOpenLabelLoading] = useState<boolean>(false);

  const openLabel = useCallback(
    async (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!labelDataPresent) return;
      setOpenLabelLoading(true);
      try {
        const url = await Storage.get(shipment.label?.labelFile?.key as string, { bucket: shipment.label?.labelFile?.bucket as string, region: shipment.label?.labelFile?.region as string });
        window.open(url, '_blank');
      } catch (error) {
        console.error(serializeError(error));
        toast.error('There was an error retrieving the label.');
      } finally {
        setOpenLabelLoading(false);
      }
    },
    [labelDataPresent, shipment]
  );

  const [resyncShipmentWithShipStation, { loading: resyncWithShipStationLoading }] = useResyncShipmentWithShipStationMutation();

  const syncWithShipStation = useCallback(
    async (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!shipment || ![TShipmentStatuses.ReadyToShip, TShipmentStatuses.PrintingLabel].includes(shipment.status)) return;
      try {
        await resyncShipmentWithShipStation({ variables: { id: shipment.id, companyId: shipment.companyId, version: shipment._version } });
        toast.success('Shipment set for re-sync with ShipStation successfully. It might take a couple of minutes for resync to be done.');
      } catch (error) {
        console.error(serializeError(error));
        toast.error('There was an error resyncing the shipment with ShipStation.');
      }
    },
    [resyncShipmentWithShipStation, shipment]
  );

  const addressVerified = useMemo(() => {
    return shipment.addressVerified;
  }, [shipment.addressVerified]);

  const topComponent = useMemo(() => {
    return (
      <div className={styles.topComponent}>
        <h2 className={styles.topTitle}>Shipment</h2>
        <div className={styles.topIcons}>
          <AlertTooltipDismiss entity={shipment} userEmail={userEmail} updateMutation={updateShipment} />
          {addressVerified === TShipStationAddressVerified.Success && (
            <Tooltip message='ShipStation address verification succeeded.'>
              <Icon className={styles.addressVerifiedSuccess} icon='check_circle' />
            </Tooltip>
          )}
          {addressVerified === TShipStationAddressVerified.Failed && (
            <Tooltip message='ShipStation address verification failed.'>
              <Icon className={styles.addressVerifiedFail} icon='error_circle_rounded' />
            </Tooltip>
          )}
          {addressVerified === TShipStationAddressVerified.Warning && (
            <Tooltip message='ShipStation address verification warning.'>
              <Icon className={styles.addressVerifiedWarning} icon='warning' />
            </Tooltip>
          )}
          {[TShipmentStatuses.ReadyToShip, TShipmentStatuses.PrintingLabel].includes(shipment.status) && (
            <Tooltip message={`Resync shipment with ShipStation.${shipment.status === TShipmentStatuses.PrintingLabel ? ` This will also try to print the label again.` : ''}`}>
              <IconButton disabled={resyncWithShipStationLoading} icon='sync' onClick={syncWithShipStation} variant='standard' />
            </Tooltip>
          )}
          {labelDataPresent && <IconButton disabled={openLabelLoading} icon='print' onClick={openLabel} variant='standard' />}
          {Array.isArray(topComponentMenuOptions) && topComponentMenuOptions.length > 0 && (
            <FloatingMenu open={topComponentMenuOpen} onOpenChange={setTopComponentMenuOpen} options={topComponentMenuOptions}>
              <IconButton icon='more_vert' onClick={() => setTopComponentMenuOpen((open) => !open)} variant='standard' />
            </FloatingMenu>
          )}
        </div>
      </div>
    );
  }, [addressVerified, labelDataPresent, openLabel, openLabelLoading, resyncWithShipStationLoading, syncWithShipStation, topComponentMenuOpen, topComponentMenuOptions, shipment, updateShipment, userEmail]);

  const isGoogleAddressDifferent = useMemo(() => {
    if (shipment.address != null && shipment.address.addressFromGoogle != null) {
      const add = shipment.address as Record<string, any>;
      const addFromGoogle = shipment.address.addressFromGoogle as Record<string, any>;
      for (const [key, v] of Object.entries(addFromGoogle)) if (add[key].toString().toLowerCase() !== v.toString().toLowerCase()) return true;

      return false;
    }

    return false;
  }, [shipment.address]);

  const sections = useMemo(() => {
    const secs: TInfoCardSectionProps<TShipmentType>[] = [];

    secs.push({
      title: 'Shipment',
      topComponent: () => topComponent,
      columns: [
        {
          label: 'Delivery Date',
          render: () => readableDate(shippingDate)
        },
        {
          label: 'Tracking Number',
          render: (data) => (data ? TrackingLink(data) : null)
        },
        {
          label: 'Company',
          render: () => shipment.company?.name
        },
        {
          label: 'Recipient',
          render: () => {
            return (
              <div className={styles.recipient}>
                {shipment.addressTypeIcon && shipment.readableAddressType && (
                  <Tooltip autoPlacement={false} message={shipment.readableAddressType} placement='bottom'>
                    <Icon filled={true} icon={shipment.addressTypeIcon} />
                  </Tooltip>
                )}
                {shipment.recipientName}
              </div>
            );
          }
        }
      ]
    });

    const shipStationColumns = [];

    if (shipment.shipStationOrderId)
      shipStationColumns.push({
        label: 'ShipStation Order ID',
        render: () => shipment.shipStationOrderId
      });

    if (shipment.shipStationShipmentId)
      shipStationColumns.push({
        label: 'ShipStation Shipment ID',
        render: () => shipment.shipStationShipmentId
      });

    if (shipment.lastShipStationResyncStartedAt != null)
      shipStationColumns.push({
        label: 'Last ShipStation Resync',
        render: () => (shipment.lastShipStationResyncStartedAt ? readableDate(shipment.lastShipStationResyncStartedAt, 'do MMM, yyyy HH:mm') : '-')
      });

    if (shipment.shipStationError)
      shipStationColumns.push({
        label: 'ShipStation Error',
        render: () => (
          <div className={styles.jsonContainer}>
            <EasyJsonViewer data={JSON.parse(shipment.shipStationError as string)} />
          </div>
        )
      });

    if (shipStationColumns.length > 0)
      secs.push({
        title: 'Shipstation',
        columns: shipStationColumns
      });

    return secs;
  }, [topComponent, shippingDate, shipment]);

  const onShipmentUpdate = useCallback(
    async (data: TShipmentExtraAttributesForm) => {
      if (!shipment.id || !shipment.companyId || !shipment._version) return;
      try {
        await updateShipment({
          variables: {
            id: shipment.id,
            companyId: shipment.companyId,
            version: shipment._version,
            input: { rack: data.rack == null ? null : data.rack, notes: data.notes == null ? null : data.notes }
          }
        });
      } catch (error) {
        console.error(serializeError(error));
        toast.error('There was an error updating the shipment.');
      }
    },
    [shipment._version, shipment.companyId, shipment.id, updateShipment]
  );

  const [addressLoading, setAddressLoading] = useState<boolean>(false);

  const onSelectAddress = useCallback(
    async (isGoogleAddress?: boolean) => {
      if (!shipment) return;
      setAddressLoading(true);
      try {
        await updateShipment({
          variables: {
            id: shipment.id,
            companyId: shipment.companyId,
            version: shipment._version,
            input: { useGoogleAddress: isGoogleAddress }
          }
        });
        toast.success('The address to use was successfully changed.');
      } catch (error) {
        console.error(serializeError(error));
        toast.error('There was an error changing which address to use.');
      } finally {
        setAddressLoading(false);
      }
    },
    [shipment, updateShipment]
  );

  const addressTopComponent = useCallback(
    (title: string, isGoogleAddress?: boolean) => {
      //console.log('addressTopComponent:shipment:', shipment);
      //console.log(title, isGoogleAddress);
      //console.log('addressTopComponent:shipment:', shipment.useGoogleAddress == null);
      const selected = shipment.useGoogleAddress == null || !shipment.address ? false : isGoogleAddress ? shipment.useGoogleAddress : !shipment.useGoogleAddress;
      return (
        <div className={styles.addressTopComponent}>
          <h2>{title}</h2>
          <Tooltip message={selected ? 'This address is selected for the shipment.' : 'Use this address for the shipment.'}>
            {selected && <IconButton disabled={addressLoading} icon='local_shipping' variant='filled_tonal' />}
            {!selected && <IconButton disabled={addressLoading} icon='local_shipping' variant='outlined' onClick={() => onSelectAddress(isGoogleAddress)} />}
          </Tooltip>
        </div>
      );
    },
    [addressLoading, onSelectAddress, shipment]
  );

  const [isEditPackageModalOpen, setIsEditPackageModalOpen] = useState<boolean>(false);

  const editPackageInfoButton = () => {
    return (
      <div className={styles.addressTopComponent}>
        <h2>Package</h2>
        <Tooltip message='Edit Package Info'>
          <IconButton
            disabled={false}
            icon='edit'
            onClick={() => {
              setIsEditPackageModalOpen(true);
            }}
            variant='outlined'
          />
        </Tooltip>
      </div>
    );
  };

  if (loading)
    return (
      <ShipmentModal headline='Shipment Details'>
        <LoadingPage />
      </ShipmentModal>
    );

  if (error) {
    console.error('query Apollo ERROR:', error);
    toast.error('An error has ocurred, check the console for details. (query Apollo ERROR)');
    //if (error.message) toast.error(error.message);
    if (!shipment)
      return (
        <ShipmentModal headline='Shipment Details'>
          <ErrorPage error={error} />
        </ShipmentModal>
      );
  }

  return (
    <>
      {isEditPackageModalOpen && (
        <EditPackageInfoModal
          shipment={shipment}
          onCancel={() => {
            setIsEditPackageModalOpen(false);
          }}
          onSuccess={() => {
            setIsEditPackageModalOpen(false);
          }}
        />
      )}
      {!_.isEmpty(confirmationProps) && <ConfirmationModal {...confirmationProps} closeModal={() => setConfirmationProps(undefined)} confirmLoading={deleteLoading || recreateLoading} onCancelClick={() => setConfirmationProps(undefined)} />}
      <ShipmentModal headline='Shipment Details'>
        <div className={styles.shipmentDetailsContent}>
          <InfoCardInner<TShipmentType> data={shipment} sections={sections} className={styles.actionDetailsContainer} />
          <div className={styles.rightPart}>
            <ControlledForm<TShipmentExtraAttributesForm>
              className={styles.form}
              fields={[
                {
                  type: 'textField',
                  label: 'Rack',
                  name: 'rack'
                },
                {
                  type: 'textArea',
                  label: 'Notes',
                  name: 'notes'
                }
              ]}
              formProps={{
                defaultValues: _.pick(shipment, 'rack', 'notes'),
                resolver: joiResolver(getUpdateShipmentExtraAttributesSchema(), {
                  convert: true,
                  abortEarly: false,
                  stripUnknown: false
                })
              }}
              onValidSubmit={onShipmentUpdate}
            />
            {shipment.packageInfo && (
              <InfoCardInner<Exclude<TShipmentType['packageInfo'], undefined | null>>
                data={shipment.packageInfo}
                sections={[
                  {
                    title: 'Package',
                    topComponent: () => editPackageInfoButton(),
                    columns: [
                      {
                        label: 'Width',
                        render: () => shipment.packageInfo?.width
                      },
                      {
                        label: 'Height',
                        render: () => shipment.packageInfo?.height
                      },
                      {
                        label: 'Length',
                        render: () => shipment.packageInfo?.length
                      },
                      {
                        label: 'Weight',
                        render: () => shipment.packageInfo?.weight
                      }
                    ]
                  }
                ]}
              />
            )}
          </div>
        </div>
        {shipment.status === TShipmentStatuses.Packaging && (
          <div className={clsx(styles.filters)}>
            <InfoCardInner
              className={styles.newActionDetailsContainer}
              data={[{}]}
              sections={[
                {
                  title: 'Override Shipping Address',
                  columns: [
                    {
                      label: 'Select Address:',
                      render: () => (
                        <Select
                          variant='outlined'
                          name='addressOverride'
                          multiple={false}
                          options={availableAddressesOpts}
                          onChange={(e) => {
                            setAddressOverride(e as string);
                          }}
                          value={addressOverride}
                        />
                      )
                    },
                    {
                      label: '',
                      render: () => (
                        <>
                          <CommonButton label='Override' onClick={onAddressOverride} variant='filled' disabled={!addressOverride || addressLoading} loading={addressLoading} />
                          {shipment.oldAddressId && <CommonButton label='Restore Address' onClick={onRestoreAddress} variant='elevated' disabled={addressLoading} loading={addressLoading} />}
                        </>
                      )
                    }
                  ]
                }
              ]}
            />
          </div>
        )}
        <div className={styles.addressContainer}>
          {shipment.address != null && (
            <>
              <InfoCardInner
                data={shipment.address}
                sections={[
                  {
                    title: isGoogleAddressDifferent ? 'Address' : undefined,
                    topComponent: isGoogleAddressDifferent ? () => addressTopComponent('Address', false) : undefined,
                    columns: [
                      {
                        label: 'Country',
                        render: () => {
                          return <CountryName name={shipment.address?.country || undefined} />;
                        }
                      },
                      {
                        label: 'State',
                        render: () => shipment.address?.state
                      },
                      {
                        label: 'City',
                        render: () => shipment.address?.city
                      },
                      {
                        label: 'Address 1',
                        render: () => shipment.address?.address1
                      },
                      {
                        label: 'Address 2',
                        render: () => shipment.address?.address2
                      },
                      {
                        label: 'Postal Code',
                        render: () => shipment.address?.zipCode
                      }
                    ]
                  }
                ]}
              />
              {shipment.address.addressFromGoogle != null && isGoogleAddressDifferent && (
                <InfoCardInner
                  data={shipment.address.addressFromGoogle}
                  sections={[
                    {
                      topComponent: () => addressTopComponent('Address from Google', true),
                      columns: [
                        {
                          label: 'Country',
                          render: () => {
                            return <CountryName name={shipment.address?.addressFromGoogle?.country || undefined} />;
                          }
                        },
                        {
                          label: 'State',
                          render: () => shipment.address?.addressFromGoogle?.state
                        },
                        {
                          label: 'City',
                          render: () => shipment.address?.addressFromGoogle?.city
                        },
                        {
                          label: 'Address 1',
                          render: () => shipment.address?.addressFromGoogle?.address1
                        },
                        {
                          label: 'Address 2',
                          render: () => shipment.address?.addressFromGoogle?.address2
                        },
                        {
                          label: 'Postal Code',
                          render: () => shipment.address?.addressFromGoogle?.zipCode
                        }
                      ]
                    }
                  ]}
                />
              )}
            </>
          )}
        </div>
      </ShipmentModal>
    </>
  );
};
